import {
  createContext,
  useSelector,
  Navigate,
  useNavigate,
  useDispatch,
  useLocation,
  useCallback,
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
} from "@/common/hooks";
import labels from "@/labels/labels";
import GridLayout from "@/layout/GridLayout";
import Routes from "@/routes/RouteComponent";
import { skipToken } from "@reduxjs/toolkit/query";
import { setTokenGenerated, setToken } from "@/store/authSlice";
import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";
import remove from "lodash/remove";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import ClosableLayout from "@/layout/ClosableLayout";
import { IconDropdown } from "@/common/components";
import { useGetNewRefreshTokenQuery } from "@/service/APIService";
import SessionPopup from "@/common/SessionPopup/SessionPopup";

export const PrivateContext = createContext();

const PrivateRoute = ({ layout, children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const cancel = () => navigate(-1);
  const [isMobileView, setIsMobileView] = useState(false);

  const checkIsMobile = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  }, []);

  const handleResize = useCallback(
    debounce(() => {
      setIsMobileView(checkIsMobile());
    }, 150),
    [checkIsMobile]
  );

  const token = useSelector((state) => state?.authReducer?.token);
  const isRiccAdministrator = useSelector(
    (state) => state?.commonReducer?.isRiccAdministrator
  );
  const showChangeProductManufacturer = useSelector(
    (state) => state?.commonReducer?.showChangeProductManufacturer
  );
  const accessorDetails = useSelector(
    (state) => state?.commonReducer?.accessorDetails
  );
  const productSponsorName = useSelector(
    (state) => state?.commonReducer?.productSponsorName
  );
  const productSponsorId = useSelector(
    (state) => state?.commonReducer?.productSponsorId
  );
  const accessProfileId = useSelector(
    (state) => state?.commonReducer?.accessProfileId
  );
  const accessProfileTypeCode = useSelector(
    (state) => state?.commonReducer?.accessProfileType
  );
  const accessorPrivileges = useSelector(
    (state) => state?.commonReducer?.accessorPrivileges
  );
  const riccAdmApprovalRequiredCode = useSelector(
    (state) => state?.commonReducer?.riccAdmApprovalRequiredCode
  );

  const debounceState = useCallback(
    debounce((value, func) => {
      func(value);
    }, 300),
    []
  );
  const debounceErrors = useCallback(
    debounce((value, func) => {
      func(value);
    }, 300),
    []
  );

  const redirectTo = (component) => {
    navigate(Routes[component].path);
  };

  const [navHeader, setNavHeader] = useState("");
  const [navCancelMessage, setNavCancelMessage] = useState("");
  const [pageHeader, setPageHeader] = useState("");
  const [subNavHeader, setSubNavHeader] = useState("");
  const [subNavButtonText, setSubNavButtonText] = useState("");
  const [subNavButtonClick, setSubNavButtonClick] = useState();
  const [navCancelFunc, setNavCancelFunc] = useState();
  const [expiryModal, setExpiryModal] = useState(false);
  const [inactiveModal, setInactiveModal] = useState(false);
  const [logoutTime, setLogoutTime] = useState(0);
  const [fetchTokenApi, setFetchTokenApi] = useState(skipToken);
  const printRef = useRef(null);

  const refreshTokenExpiryTime = (token?.refreshExpiresIn - 5 * 60) * 1000;
  const tokenGeneratedTime = token?.generatedAt;
  let sessionExpiryInterval = "";
  let sessionInactiveTimer = "";
  const sessionInactiveAlert = 1000 * 60 * 5;

  const { data: newToken, isLoading } =
    useGetNewRefreshTokenQuery(fetchTokenApi);

  const actionsDropdown = (props) => {
    return <IconDropdown {...props} list={props.list} />;
  };

  const hasAccessToUpdate = () => {
    return (
      isRiccAdministrator || accessProfileId === "4" || accessProfileId === "2"
    );
  };

  const handleSessionExpiry = () => {
    if (token?.accessToken && token?.generatedAt) {
      const currentTime = new Date().getTime();
      if (currentTime - tokenGeneratedTime > refreshTokenExpiryTime) {
        setExpiryModal(true);
        setLogoutTime(new Date().getTime() + 5 * 60 * 1000);
        clearInterval(sessionExpiryInterval);
      }
    }
  };

  const continueSession = (type) => {
    setLogoutTime(0);
    if (type === "sessionExpiry") {
      setFetchTokenApi(1);
      setExpiryModal(false);
    } else {
      setInactiveModal(false);
      clearTimeout(sessionInactiveTimer);
    }
  };

  const exitSession = () => {
    clearTimeout(sessionInactiveTimer);
    dispatch(setToken({}));
    navigate("/logout");
  };

  const setSessionExpiryAlert = () => {
    if (sessionInactiveTimer) {
      clearTimeout(sessionInactiveTimer);
    }

    sessionInactiveTimer = setTimeout(() => {
      setLogoutTime(new Date().getTime() + 5 * 60 * 1000);
      setInactiveModal(true);
    }, sessionInactiveAlert);
  };

  useLayoutEffect(() => {
    setNavCancelFunc();
  }, [location.pathname]);

  useEffect(() => {
    sessionExpiryInterval = setInterval(handleSessionExpiry, 1000);
  }, [token]);

  useEffect(() => {
    if (newToken) {
      dispatch(setToken(newToken));
      dispatch(setTokenGenerated(new Date().getTime()));
    }
  }, [isLoading]);

  useEffect(() => {
    if (!token?.generatedAt) {
      dispatch(setTokenGenerated(new Date().getTime()));
    }

    const events = ["load", "mousemove", "click", "scroll", "keypress"];
    for (let e in events) {
      window.addEventListener(events[e], setSessionExpiryAlert);
    }

    return () => {
      for (let e in events) {
        window.removeEventListener(events[e], setSessionExpiryAlert);
      }
    };
  }, []);

  useLayoutEffect(() => {
    setIsMobileView(checkIsMobile());
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel && handleResize.cancel();
    };
  }, [checkIsMobile, handleResize]);

  if (token?.access_token) {
    return (
      <PrivateContext.Provider
        value={{
          labels,
          isMobileView,
          Routes,
          skipToken,
          navigate,
          dispatch,
          location,
          cancel,
          debounceState,
          debounceErrors,
          cloneDeep,
          sortBy,
          uniqBy,
          remove,
          uniqWith,
          isEqual,
          setNavHeader,
          navHeader,
          setPageHeader,
          pageHeader,
          subNavHeader,
          setSubNavHeader,
          subNavButtonText,
          setSubNavButtonText,
          subNavButtonClick,
          setSubNavButtonClick,
          navCancelFunc,
          setNavCancelFunc,
          useSelector,
          redirectTo,
          isRiccAdministrator,
          showChangeProductManufacturer,
          riccAdmApprovalRequiredCode,
          productSponsorId,
          accessorDetails,
          productSponsorName,
          accessProfileId,
          accessProfileTypeCode,
          accessorPrivileges,
          navCancelMessage,
          setNavCancelMessage,
          printRef,
          actionsDropdown,
          hasAccessToUpdate,
        }}
      >
        <SessionPopup
          type="sessionExpiry"
          showModal={expiryModal}
          primaryButtonClicked={() => continueSession("sessionExpiry")}
          secondaryButtonClicked={() => exitSession()}
          logoutTime={logoutTime}
        />

        <SessionPopup
          type="sessionInactive"
          showModal={inactiveModal}
          primaryButtonClicked={() => continueSession("sessionInactive")}
          secondaryButtonClicked={() => exitSession()}
          logoutTime={logoutTime}
        />

        {layout === "GridLayout" ? (
          <GridLayout>{children}</GridLayout>
        ) : layout === "ClosableLayout" ? (
          <ClosableLayout>{children}</ClosableLayout>
        ) : (
          <main className="container">{children}</main>
        )}
      </PrivateContext.Provider>
    );
  } else {
    return <Navigate to={Routes.login.path} />;
  }
};

export default PrivateRoute;
